import { useState, useEffect } from 'react';

/* API */
import { api } from '../../api';

/* CSS */
import shared from '../../styles/shared.module.css';
import styles from './EmailChangeModal.module.css';

/*
 * Modal shown when a user changes their email in Settings.
 * They enter the 6-digit code sent to the NEW address; only on success does
 * the email actually switch (onConfirmed receives the updated user).
 */
const EmailChangeModal = ({ isOpen, newEmail, onClose, onConfirmed }) => {
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [timer, setTimer] = useState(59);

    // Reset everything each time the modal opens
    useEffect(() => {
        if (isOpen) {
            setTimer(59);
            setCode('');
            setError('');
            setInfo('');
        }
    }, [isOpen]);

    // Count the resend cooldown down to 0 (only while open)
    useEffect(() => {
        if (!isOpen || timer === 0) return;
        const id = setInterval(() => setTimer((prev) => prev - 1), 1000);
        return () => clearInterval(id);
    }, [isOpen, timer]);

    if (!isOpen) return null;

    const formatTime = (seconds) => {
        const secs = seconds < 10 ? `0${seconds}` : seconds;
        return `00:${secs}`;
    };

    const handleVerify = async () => {
        if (isLoading) return;
        try {
            setIsLoading(true);
            setError('');
            const result = await api.post('/auth/confirm-email-change', { newEmail, code });
            onConfirmed(result.user);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return; // still on cooldown
        try {
            setError('');
            setInfo('');
            setTimer(59);
            await api.post('/auth/request-email-change', { newEmail });
            setInfo('A new code is on its way.');
        } catch (err) {
            setError(err.message || 'Could not resend the code.');
        }
    };

    return (
        // Click the dark backdrop to dismiss; stop clicks inside the box from bubbling out
        <div className={shared.modalOverlay} onClick={onClose}>
            <div className={shared.modalContent} onClick={(e) => e.stopPropagation()}>
                <h2 className={styles.title}>Confirm your new email</h2>
                <p className={styles.message}>
                    Enter the 6-digit code we sent to <strong>{newEmail}</strong> to confirm it's yours.
                </p>

                <input
                    type="text"
                    className={shared.codeInput}
                    maxLength="6"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
                    placeholder="------"
                    autoFocus
                />

                {error && <div className={styles.error}>{error}</div>}
                {info && <div className={styles.info}>{info}</div>}

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={`${shared.modalButton} ${shared.secondary}`}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`${shared.modalButton} ${shared.primary}`}
                        onClick={handleVerify}
                    >
                        <div className={shared.buttonContent}>
                            {isLoading ? 'Verifying...' : 'Confirm'}
                            {isLoading && <span className={shared.spinner}></span>}
                        </div>
                    </button>
                </div>

                <button
                    type="button"
                    className={styles.resend}
                    disabled={timer > 0}
                    onClick={handleResend}
                >
                    {timer > 0 ? `Resend code (${formatTime(timer)})` : 'Resend code'}
                </button>
            </div>
        </div>
    );
};

export default EmailChangeModal;
